// BrandProfilePage.jsx
import React, { useState } from 'react';
import {
    Typography, Box, Button, Chip, Stack, Paper, Avatar,
    Link, CircularProgress, Alert, Container
} from '@mui/material';
import {
    Edit, ArrowBack, Link as LinkIcon, Business,
    Category, AttachMoney, Description, Person
} from '@mui/icons-material';
import BrandProfileForm from './BrandProfileForm';
import useBrandProfile from '../../Hooks/useBrandProfile';
import ProfileSection from '../../components/ProfileSection';
import resolveImageUrl from '../../utils/resolveImageUrl';
import { brandGradient } from '../../shared-theme/themePrimitives';

export default function BrandProfilePage() {
    const { profile, loading, error, fetchProfile } = useBrandProfile();
    const [editing, setEditing] = useState(false);

    const handleSave = async () => {
        await fetchProfile();
        setEditing(false);
    };

    const handleEdit = () => {
        setEditing(true);
    };

    const handleCancel = () => {
        setEditing(false);
    };

    // Show loading state
    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', my: 4 }}>
                <Alert severity="error">
                    Failed to load brand profile. Please try again.
                </Alert>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button onClick={fetchProfile} variant="contained">
                        Retry
                    </Button>
                </Box>
            </Box>
        );
    }

    if (editing || !profile) {
        return (
            <Box sx={{ position: 'relative' }}>
                {profile && (
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={handleCancel}
                        color="secondary"
                        sx={{ position: 'absolute', left: 8, top: 8, zIndex: 1 }}
                    >
                        Cancel
                    </Button>
                )}
                <BrandProfileForm
                    onSave={handleSave}
                    defaultValues={profile}
                    isEditing={!!profile}
                />
            </Box>
        );
    }

    const coverUrl = resolveImageUrl(profile?.brand?.cover_image);
    const avatarUrl = resolveImageUrl(profile?.brand?.profile_image);
    const displayName = profile.brand_name || profile.name;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
                {/* Cover + identity */}
                <Box
                    sx={{
                        height: { xs: 100, sm: 140 },
                        background: coverUrl
                            ? `linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.35)), url(${coverUrl})`
                            : brandGradient,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-end' },
                    gap: 2,
                    px: 3,
                    pb: 3,
                    mt: '-48px',
                    textAlign: { xs: 'center', sm: 'left' },
                }}>
                    <Avatar
                        src={avatarUrl || undefined}
                        sx={{
                            width: 96,
                            height: 96,
                            border: '4px solid',
                            borderColor: 'background.paper',
                            boxShadow: 2,
                            fontSize: '2rem',
                            fontWeight: 700,
                            bgcolor: 'secondary.main',
                        }}
                    >
                        {displayName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" fontWeight={700}>{displayName}</Typography>
                        {profile.industry && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Business fontSize="small" /> {profile.industry}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Edit />}
                        onClick={handleEdit}
                        sx={{ borderRadius: '999px', px: 3, flexShrink: 0 }}
                    >
                        Edit Profile
                    </Button>
                </Box>

                <Box sx={{ px: 3, pb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ProfileSection icon={<Business fontSize="small" />} title="Brand Details">
                                <Stack direction="row" spacing={4} flexWrap="wrap">
                                    <Box>
                                        <Typography sx={{ color: 'secondary.main', display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Person fontSize="small" /> Role
                                        </Typography>
                                        <Typography ml={3}>{profile.brand.role_in_brand || 'Not specified'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'secondary.main', display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <AttachMoney fontSize="small" /> Budget
                                        </Typography>
                                        <Typography ml={3}>{profile.brand.annual_budget || 'Not specified'}</Typography>
                                    </Box>
                                </Stack>
                            </ProfileSection>

                            <ProfileSection icon={<Category fontSize="small" />} title="Categories">
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {profile.brand.category && profile.brand.category.length > 0 ? (
                                        profile.brand.category.map(cat => (
                                            <Chip key={cat} label={cat} color="secondary" variant="outlined" />
                                        ))
                                    ) : (
                                        <Typography color="text.secondary">No categories specified</Typography>
                                    )}
                                </Stack>
                            </ProfileSection>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ProfileSection icon={<Description fontSize="small" />} title="About">
                                <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                    {profile?.brand?.description || 'No description provided'}
                                </Typography>
                            </ProfileSection>

                            <ProfileSection icon={<LinkIcon fontSize="small" />} title="Social Links">
                                <Stack spacing={1.5}>
                                    {profile.brand.profile_links && profile.brand.profile_links.length > 0 ? (
                                        profile.brand.profile_links
                                            .filter(link => link && link.trim())
                                            .map((link, idx) => (
                                                <Link
                                                    key={idx}
                                                    href={link.trim()}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    color="secondary.main"
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    <LinkIcon fontSize="small" />
                                                    <Typography>
                                                        {link.trim().replace(/(^\w+:|^)\/\//, '')}
                                                    </Typography>
                                                </Link>
                                            ))
                                    ) : (
                                        <Typography color="text.secondary">
                                            No social links provided
                                        </Typography>
                                    )}
                                </Stack>
                            </ProfileSection>

                            <ProfileSection icon={<Business fontSize="small" />} title="Target Platforms" sx={{ mb: 0 }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {profile.brand.target_platforms && profile.brand.target_platforms.length > 0 ? (
                                        profile.brand.target_platforms.map(platform => (
                                            <Chip key={platform} label={platform} color="secondary" variant="outlined" />
                                        ))
                                    ) : (
                                        <Typography color="text.secondary">No target platforms specified</Typography>
                                    )}
                                </Stack>
                            </ProfileSection>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
}
